import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Table,
  Typography
} from "antd";
import { SortOrder } from "antd/lib/table/interface";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { ProductChangeSource } from "../../../constants/productChangeSource";
import { ProductSyncChangeType } from "../../../constants/productSyncChangeType";
import { CompanyProductLogModel } from "../../../models/company-product-log-interface";
import { CustomerServiceQueries } from "../../../queries/customer-service-queries";

const { Title } = Typography;

type Props = {
  numDays: number;
};

const CPQProductsTab: React.FC<Props> = ({
  numDays
}) => {
  const history = useHistory();
  const [search, setSearch] = useState<string|null>(null);
  const [filteredData, setFilteredData] = useState<CompanyProductLogModel[]|null>(null);
  const useGetProductLogs = CustomerServiceQueries.useCompanyProductLogs(numDays);

  useEffect(() => {
    if(!!search) {
      const searchLower = search.toLocaleLowerCase();
      const data = useGetProductLogs.data?.filter(x => x.companyName?.toLocaleLowerCase().includes(searchLower) ||
        x.featureName?.toLocaleLowerCase().includes(searchLower) ||
        x.productName?.toLocaleLowerCase().includes(searchLower) ||
        x.userName?.toLocaleLowerCase().includes(searchLower));
      setFilteredData(data ?? []);
    } else {
      setFilteredData(null);
    }
  }, [search])

  const columns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      render: (companyName: string, record: CompanyProductLogModel) => {
        return (
          <Button
            type="link"
            href={`/admin/company/${record.companyId}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/admin/company/${record.companyId}`);
            }}
          >
            {companyName}
          </Button>
        );
      },
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      render: (productName: string, record: CompanyProductLogModel) => {
        return (
          <Button
            type="link"
            href={`/admin/products/${record.productId}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/admin/products/${record.productId}`);
            }}
          >
            {productName}
          </Button>
        );
      },
    },
    {
      title: "Product Version",
      dataIndex: "productVersion",
      key: "productVersion",
      sorter: {
        compare: (a: CompanyProductLogModel, b: CompanyProductLogModel) => a.productVersion - b.productVersion
      }
    },
    {
      title: "Sync Change Type",
      dataIndex: "syncChangeTypeId",
      key: "syncChangeTypeId",
      render: (syncChangeTypeId: number) => {
        return Object.keys(ProductSyncChangeType).find(key => ProductSyncChangeType[key] === syncChangeTypeId)?.replace(/([a-z])([A-Z])/g, `$1 $2`);
      },
      filters: [...Object.values(ProductSyncChangeType).filter(x => isNaN(x)).map((value, index) => {
        return { text: value.replace(/([a-z])([A-Z])/g, `$1 $2`), value: index + 1 }
      })],
      onFilter: (value: number, record: CompanyProductLogModel) => {
        return record.syncChangeTypeId === value;
      }
    },
    {
      title: "Source",
      dataIndex: "sourceId",
      key: "source",
      render: (sourceId: number) => {
        return Object.keys(ProductChangeSource).find(key => ProductChangeSource[key] === sourceId)?.replace(/([a-z])([A-Z])/g, `$1 $2`);
      },
      filters: [...Object.values(ProductChangeSource).filter(x => isNaN(x)).map((value, index) => {
        return { text: value.replace(/([a-z])([A-Z])/g, `$1 $2`), value: index + 1 }
      })],
      onFilter: (value: number, record: CompanyProductLogModel) => {
        return record.sourceId === value;
      }
    },
    {
        title: "User",
        dataIndex: "userName",
        key: "userName",
        render: (userName: string, record: CompanyProductLogModel) => {
          return (
            <Button
              type="link"
              href={`/admin/users/manage/edit/${record.userId}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/users/manage/edit/${record.userId}`);
              }}
            >
              {userName}
            </Button>
          );
        },
        sorter: {
          compare: (a: CompanyProductLogModel, b: CompanyProductLogModel) => (a.userName ?? "").localeCompare(b.userName ?? "")
        }
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => {
        return new Date(date + "Z").toLocaleString();
      },
      sorter: {
        compare: (a, b) => {
            let d1 = a.date ?? new Date("01/01/1000");
            let d2 = b.date ?? new Date("01/01/1000");
            return moment(d1).diff(moment(d2));
        }
      },
      defaultSortOrder: 'descend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    },
  ];

  return (
    <Card style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
      <Col flex={"auto"}>
        </Col>
        <Col>
          <Input
            placeholder="Search Logs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col span={24}>
          <Table
            className="full-width"
            dataSource={filteredData ?? useGetProductLogs.data ?? []}
            rowKey={(data) => data.id}
            columns={columns}
            loading={useGetProductLogs.isLoading}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default CPQProductsTab;
